import {
  LoginCredentials,
  RegisterCredentials,
  ResetPasswordData,
  SocialProvider,
  User,
  mapSupabaseUserToUser,
} from '../types/auth';

import { AuthErrorHandler, withErrorHandling } from './errorHandler';
import { LogoutManager } from './logoutManager';
import { supabase } from './supabase';
// Directus同期処理のインポートを削除（Supabase認証のみに移行）
// import { syncNewUser, syncUserAfterAuth } from './userSync';

// Constants
const SUPABASE_NOT_INITIALIZED_ERROR = 'Supabase client is not initialized';

const USER_REGISTRATION_CONTEXT = 'user registration';
const USER_LOGIN_CONTEXT = 'user login';

/**
 * ユーザー登録
 */
export const registerUser = async (credentials: RegisterCredentials): Promise<User> => {
  try {
    if (!supabase) {
      throw new Error(SUPABASE_NOT_INITIALIZED_ERROR);
    }

    // 登録前に既存ユーザーかどうかをチェック
    try {
      const { data: existingUserData } = await supabase.auth.signInWithPassword({
        email: credentials.email,
        password: credentials.password,
      });

      if (existingUserData?.user) {
        // 既存ユーザーとしてログインできた場合
        // 既存ユーザーエラーを投げる
        throw new Error('USER_ALREADY_EXISTS');
      }
    } catch (existingUserCheckError) {
      // 既存ユーザーでない場合のみ、新規登録を続行
      if (
        existingUserCheckError &&
        typeof existingUserCheckError === 'object' &&
        'type' in existingUserCheckError
      ) {
        // 既存ユーザーエラーの場合は再スロー
        throw existingUserCheckError;
      }
      // その他のエラー（パスワードが間違っているなど）は新規登録を続行
    }

    const { data, error } = await supabase.auth.signUp({
      email: credentials.email,
      password: credentials.password,
      options: {
        data: {
          username: credentials.username,
        },
        emailRedirectTo: `${window.location.origin}/?confirmed=true`,
      },
    });

    // エラーチェックと処理
    if (error) {
      throw AuthErrorHandler.handleAuthError(error, USER_REGISTRATION_CONTEXT);
    }

    // ユーザーが作成されていない場合
    if (!data.user) {
      throw AuthErrorHandler.handleAuthError(
        new Error('Registration failed - no user returned'),
        USER_REGISTRATION_CONTEXT
      );
    }

    // 隠れたエラーのチェック
    checkForHiddenErrors(data);

    // Directus同期処理を削除（Supabase認証のみに移行）
    // 新規ユーザーはSupabaseのみで管理

    return mapSupabaseUserToUser(data.user);
  } catch (error) {
    // エラーが既にAuthErrorの場合はそのまま再スロー
    if (error && typeof error === 'object' && 'type' in error) {
      throw error;
    }

    // その他のエラーは処理してから再スロー
    throw AuthErrorHandler.handleAuthError(error, USER_REGISTRATION_CONTEXT);
  }
};

/**
 * Supabaseのレスポンスに隠れたエラーがないかチェック
 */
const checkForHiddenErrors = (data: any): void => {
  if (!data || typeof data !== 'object') return;

  // dataオブジェクト内にエラー情報がないかチェック
  if (data.error) {
    throw AuthErrorHandler.handleAuthError(data.error, USER_REGISTRATION_CONTEXT);
  }

  // 確認メール送信の失敗を示すフラグがないかチェック
  if (data.email_send_failed || data.confirmation_email_failed) {
    const emailError = {
      code: 'unexpected_failure',
      message: 'Error sending confirmation email',
    };
    throw AuthErrorHandler.handleAuthError(emailError, USER_REGISTRATION_CONTEXT);
  }
};

/**
 * ユーザーログイン
 */
export const loginUser = async (credentials: LoginCredentials): Promise<User> => {
  return withErrorHandling(
    async () => {
      if (!supabase) {
        throw new Error(SUPABASE_NOT_INITIALIZED_ERROR);
      }

      const { data, error } = await supabase.auth.signInWithPassword({
        email: credentials.email,
        password: credentials.password,
      });

      if (error) {
        throw AuthErrorHandler.handleAuthError(error, USER_LOGIN_CONTEXT);
      }

      if (!data.user) {
        throw AuthErrorHandler.handleAuthError(
          new Error('Login failed - no user returned'),
          USER_LOGIN_CONTEXT
        );
      }

      // Directus同期処理を削除（Supabase認証のみに移行）

      return mapSupabaseUserToUser(data.user);
    },
    USER_LOGIN_CONTEXT,
    { maxRetries: 2 }
  );
};

/**
 * ユーザーログアウト
 */
export const logoutUser = async (redirectTo?: string): Promise<void> => {
  return withErrorHandling(
    async () => {
      // LogoutManagerを使用して完全ログアウト
      await LogoutManager.performCompleteLogout(redirectTo);
    },
    'user logout',
    { maxRetries: 1 }
  );
};

/**
 * ソーシャルログイン
 */
export const loginWithSocial = async (provider: SocialProvider): Promise<void> => {
  return withErrorHandling(
    async () => {
      if (!supabase) {
        throw new Error(SUPABASE_NOT_INITIALIZED_ERROR);
      }

      // プロバイダー固有の設定
      const providerOptions: Record<SocialProvider, any> = {
        google: {
          scopes: 'email profile',
        },
        apple: {
          scopes: 'email name',
        },
        twitter: {
          // Twitter OAuth 2.0 settings
        },
      };

      const { error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
          ...providerOptions[provider],
        },
      });

      if (error) {
        throw AuthErrorHandler.handleAuthError(error, 'social login');
      }

      // OAuth認証の場合はリダイレクトが発生するため、ここでは何もしない
    },
    'social login',
    { maxRetries: 1 }
  );
};

/**
 * パスワードリセット
 */
export const resetPassword = async (data: ResetPasswordData): Promise<void> => {
  return withErrorHandling(
    async () => {
      if (!supabase) {
        throw new Error(SUPABASE_NOT_INITIALIZED_ERROR);
      }

      // セキュリティのため、メールアドレスの存在確認は行わず
      // 常に成功メッセージを表示する
      // これにより、攻撃者が登録されているメールアドレスを特定できない

      const { error } = await supabase.auth.resetPasswordForEmail(data.email, {
        redirectTo: `${window.location.origin}/auth/reset-password`,
      });

      if (error) {
        throw AuthErrorHandler.handleAuthError(error, 'password reset');
      }

      // 成功時は何も返さない（フロントエンドで適切なメッセージを表示）
    },
    'password reset',
    { maxRetries: 2 }
  );
};

/**
 * 認証状態の変更を監視
 */
export const onAuthStateChange = (callback: (event: string, session: any) => void) => {
  if (!supabase) {
    console.warn(`${SUPABASE_NOT_INITIALIZED_ERROR}, auth state change listener not available`);
    // SupabaseのonAuthStateChangeと同じ構造を返す
    return {
      data: {
        subscription: {
          unsubscribe: () => {
            // 何もしない（ダミーのunsubscribe関数）
          },
        },
      },
      error: null,
    };
  }
  return supabase.auth.onAuthStateChange(callback);
};
